import React, { useState } from 'react';
import { Modal } from '../../common/Modal';
import InputField from '../../../components/common/InputField';
import FileUpload from '../../../components/common/FileUpload';
import Editor from '../../../components/common/Editor';
import { PartialBlock } from '@blocknote/core';
import axios from 'axios';
import { decodeJWT } from '../../../utils/jwt';

interface CreateNewsPostModalProps {
    isOpen: boolean;
    closeModal: () => void;
    refresh: () => void;
}

const CreateNewsPostModal: React.FC<CreateNewsPostModalProps> = ({ isOpen, closeModal, refresh }) => {
    const initialJsonData = {
        title: '',
        description: '',
        tag: '',
        featured: false,
        cover: '/posts/post-default.jpg',
        content: [] as PartialBlock[],
    };

    const [jsonData, setJsonData] = useState(initialJsonData);

    const handleChange = (name: string, value: string | File | boolean | null) => {
        setJsonData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditorChange = (content: PartialBlock[]) => {
        setJsonData((prev) => ({ ...prev, content }));
    };

    const handleCancel = () => {
        setJsonData(initialJsonData);
        closeModal();
    };

    const handleSubmit = async () => {
        const accessToken = sessionStorage.getItem('access_token');

        if (!jsonData.title || !jsonData.description || !jsonData.tag) return;

        const year = new Date().getFullYear();

        const payload = {
            ...jsonData,
            content: JSON.stringify(jsonData.content),
            slug: `/${year}/${jsonData.title.toLowerCase().replaceAll(' ', '-')}`,
            author: decodeJWT(accessToken)?.nickname || '',
            created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        };

        try {
            if ((jsonData.cover) as any instanceof File) {
                const formData = new FormData();
                formData.append('images', jsonData.cover);

                const uploadResponse = await axios.post('https://api.robinhoodboechout.be/api/v1/albums/posts', formData, {
                     headers: {
                         'Content-Type': 'multipart/form-data',
                         Authorization: `Bearer ${accessToken}`,
                     },
                 });

                if (uploadResponse.data?.images[0]?.filename) {
                    payload.cover = `/posts/${uploadResponse.data.images[0].filename}`;
                }
            }

            await axios.post(`https://api.robinhoodboechout.be/api/v1/posts`, payload, {
                 headers: {
                     'Content-Type': 'application/json',
                     Authorization: `Bearer ${accessToken}`,
                 },
            });

            refresh();
            handleCancel();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={closeModal} className='p-6 lg:p-10'>
            <div className='flex flex-col p-6'>
                <h4 className='text-2xl mb-1 font-semibold text-slate-700'>
                    Nieuwsbericht maken
                </h4>
                <p className='mb-3 mt-1 text-slate-400'>
                    Vul de nodige informatie in om een nieuwsbericht te maken.
                </p>

                {/* Row 1: Titel & Beschrijving */}
                <div className='flex w-full gap-x-4 mt-4'>
                    <div className='w-full flex-1 min-w-[200px]'>
                        <label className='block mb-1 text-sm text-slate-700'>
                            Titel
                        </label>
                        <InputField name={'title'} type={'text'} required={true} placeholder={'Titel'} value={jsonData.title} onChange={handleChange}/>
                    </div>
                    <div className='w-full flex-1 min-w-[200px]'>
                        <label className='block mb-1 text-sm text-slate-700'>
                            Beschrijving
                        </label>
                        <InputField name={'description'} type={'text'} required={true} placeholder={'Beschrijving'} value={jsonData.description} onChange={handleChange}/>
                    </div>
                </div>

                {/* Row 2: Tag & Featured */}
                <div className='flex w-full gap-x-4 mt-4'>
                    <div className='w-full flex-1 min-w-[200px]'>
                        <label className='block mb-1 text-sm text-slate-700'>
                            Tag
                        </label>
                        <InputField name={'tag'} type={'text'} required={true} placeholder={'Tag'} value={jsonData.tag} onChange={handleChange}/>
                    </div>
                    <div className='w-full flex-1 min-w-[200px]'>
                        <label className='block mb-1 text-sm text-slate-700'>
                            Aanbevolen
                        </label>
                        <input type='checkbox' name='featured' className='cursor-pointer absolute w-20 h-12 peer appearance-none rounded-md' checked={jsonData.featured} onChange={(e) => handleChange(e.target.name, e.target.checked)}/>
                        <span className='w-20 mt-2 flex items-center flex-shrink-0 p-1 bg-gray-200 rounded-full duration-300 ease-in-out peer-checked:bg-accent after:w-8 after:h-8 after:bg-white after:rounded-full after:shadow-md after:duration-300 peer-checked:after:translate-x-10 group-hover:after:translate-x-10'></span>
                    </div>
                </div>

                {/* Row 3: File Upload */}
                <div className='w-full min-w-[200px] mt-4'>
                    <label className='block mb-1 text-sm text-slate-700'>
                        Cover
                    </label>
                    <FileUpload name={'cover'} required={true} onChange={handleChange}/>
                </div>

                {/* Row 4: Content (Full width) */}
                <div className='w-full min-w-[200px] mt-4'>
                    <label className='block mb-1 text-sm text-slate-700'>
                        Content
                    </label>
                    <Editor content={jsonData.content} isEditable={true} setContent={handleEditorChange}/>
                </div>
            </div>

            {/* Action Buttons: Cancel and Publish */}
            <div className='p-6 pt-0'>
                <div className='flex space-x-2'>
                    <button
                        className='w-full mx-auto select-none rounded border border-red-600 py-2 px-4 text-center text-sm font-semibold text-red-600 transition-all hover:bg-red-600 hover:text-white hover:shadow-md hover:shadow-red-600/20 active:bg-red-700 active:text-white active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none'
                        onClick={handleCancel}>
                        Annuleren
                    </button>

                    <button
                        className='w-full mx-auto select-none rounded bg-primary py-2 px-4 text-center text-sm font-semibold text-white shadow-md shadow-slate-900/10 transition-all hover:shadow-lg hover:shadow-slate-900/20 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none'
                        type='button'
                        onClick={handleSubmit}>
                        Publiceren
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateNewsPostModal;